import * as React from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { getAccessStatus, type AccessStatus } from "./access";

/* Persist access status so reloads don't spinner.
   Stored alongside the auth session — guaranteed to be the same user. */
const ACCESS_CACHE_KEY = "arabise-access-v1";
const SUPABASE_AUTH_STORAGE_KEY = "arabise-auth";   // matches storageKey in supabase.ts

function isValidCachedAccess(v: any): v is AccessStatus {
  return !!(v
    && typeof v === "object"
    && v.user && typeof v.user === "object"
    && v.profile && typeof v.profile === "object"
    && typeof v.profile.license_status === "string");
}

function readCachedAccess(): AccessStatus | null {
  try {
    const raw = localStorage.getItem(ACCESS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isValidCachedAccess(parsed)) {
      // Old / malformed cache — drop it so we re-fetch cleanly.
      localStorage.removeItem(ACCESS_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    try { localStorage.removeItem(ACCESS_CACHE_KEY); } catch {}
    return null;
  }
}
function writeCachedAccess(a: AccessStatus | null) {
  try {
    if (a) localStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify(a));
    else   localStorage.removeItem(ACCESS_CACHE_KEY);
  } catch { /* ignore */ }
}

/**
 * Synchronously parse the Supabase session out of localStorage so we
 * can seed React state at boot — otherwise route guards see
 * `session: null` while `getSession()` is still in flight and bounce
 * the user to /login, causing the black/glimpse/black flicker.
 *
 * Returns a minimal Session-shaped object if the stored token isn't
 * expired; null otherwise. The real `getSession()` reconciles in the
 * background.
 */
function readCachedSession(): Session | null {
  try {
    const raw = localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // supabase-js v2 stores either {currentSession, expiresAt} or a flat session.
    const s = parsed?.currentSession || parsed?.session || parsed;
    if (!s || !s.access_token) return null;
    if (s.expires_at && Date.now() / 1000 >= s.expires_at) return null;
    return s as Session;
  } catch {
    return null;
  }
}

type AuthState = {
  /** Initial getSession() is still in flight. */
  loading: boolean;
  session: Session | null;
  user:    User | null;
  /** Profile / license info — null while loading OR signed-out. */
  access:  AccessStatus | null;
  /** Force-refresh the access status (after a profile/license write). */
  refreshAccess: () => Promise<void>;
  signOut:       (redirectTo?: string) => Promise<void>;
};

const Ctx = React.createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Synchronously hydrate both session AND access from localStorage so
  // route guards don't see a "no session" frame and redirect to /login
  // before the real getSession() resolves.
  const cachedSession = readCachedSession();
  const cachedAccess  = readCachedAccess();
  // We can skip the spinner once we have BOTH: a non-expired session AND
  // the access status it implies. If either is missing, fall back to the
  // network round-trip with the spinner.
  const optimistic = !!(cachedSession && cachedAccess);

  const [loading, setLoading] = React.useState(!optimistic);
  const [session, setSession] = React.useState<Session | null>(cachedSession);
  const [access,  setAccess]  = React.useState<AccessStatus | null>(cachedAccess);

  const refreshAccess = React.useCallback(async () => {
    try {
      const a = await getAccessStatus();
      setAccess(a);
      writeCachedAccess(a);
    } catch (e) {
      console.warn("[auth] refreshAccess failed:", e);
      setAccess(null);
      writeCachedAccess(null);
    }
  }, []);

  // Initial session + access load.
  React.useEffect(() => {
    let cancelled = false;

    // Safety net: never sit on the spinner for more than 4s.
    // If supabase.auth.getSession() or my_profile() hangs (e.g. stale
    // token, offline, DNS hiccup), unblock the UI as signed-out so
    // the user can at least see the login form.
    const safetyTimer = window.setTimeout(() => {
      if (!cancelled) {
        console.warn("[auth] init timed out after 4s — unblocking UI");
        setLoading(false);
      }
    }, 4000);

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        setSession(session);
        if (session) {
          // Race refreshAccess against a 3s timer so a slow RPC doesn't
          // keep the spinner up.
          await Promise.race([
            refreshAccess(),
            new Promise(res => window.setTimeout(res, 3000))
          ]);
        }
      } catch (e) {
        console.warn("[auth] initial getSession failed:", e);
      } finally {
        window.clearTimeout(safetyTimer);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; window.clearTimeout(safetyTimer); };
  }, [refreshAccess]);

  // Subscribe to auth changes.
  React.useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession);
      if (newSession) {
        await refreshAccess();
      } else if (event === "SIGNED_OUT") {
        setAccess(null);
        writeCachedAccess(null);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [refreshAccess]);

  // Bridge: when modes fire the legacy "arabise:points-updated" event,
  // we refresh access so the dashboard pill updates.
  React.useEffect(() => {
    const onPoints = () => { void refreshAccess(); };
    window.addEventListener("arabise:points-updated", onPoints as EventListener);
    return () => window.removeEventListener("arabise:points-updated", onPoints as EventListener);
  }, [refreshAccess]);

  const signOut = React.useCallback(async (redirectTo = "/signed-out") => {
    try { await supabase.auth.signOut(); } catch (e) { console.warn("[auth] signOut", e); }
    setSession(null);
    setAccess(null);
    writeCachedAccess(null);
    window.location.assign(redirectTo);
  }, []);

  const value = React.useMemo<AuthState>(() => ({
    loading,
    session,
    user: session?.user ?? null,
    access,
    refreshAccess,
    signOut
  }), [loading, session, access, refreshAccess, signOut]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

export function useUser():    User | null            { return useAuth().user; }
export function useSession(): Session | null         { return useAuth().session; }
export function useAccess():  AccessStatus | null    { return useAuth().access; }
